import React, { useState, useEffect } from 'react';
import Card from '../Common/Card';
import Button from '../Common/Button';
import Select from '../Common/Select';
import api from '../../utils/api';
import { showSuccess, showError, showDeleteConfirm } from '../../utils/swal';
import { Plus, Trash2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const InterestedPortfolios = ({ customerId }) => {
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState([]);
  const [allPortfolios, setAllPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchCustomerPortfolios();
    fetchAllPortfolios();
  }, [customerId]);

  const fetchCustomerPortfolios = async () => {
    try {
      const response = await api.get(`/customers/${customerId}`);
      console.log('Customer data:', response.data);
      // Laravel camelCase veya snake_case olabilir
      const portfolioData = response.data.interested_portfolios || response.data.interestedPortfolios || [];
      console.log('Interested portfolios:', portfolioData);
      setPortfolios(portfolioData);
      setLoading(false);
    } catch (error) {
      console.error('Portföyler yüklenemedi:', error);
      setLoading(false);
    }
  };

  const fetchAllPortfolios = async () => {
    try {
      const response = await api.get('/portfolios');
      const allPortfolioData = response.data.data || response.data;
      const portfolioOptions = allPortfolioData.map(p => ({
        value: p.id,
        label: `${p.title} - ${p.city ?? p.city.name} ${p.district ?? p.district.name}`,
        portfolio: p
      }));
      setAllPortfolios(portfolioOptions);
    } catch (error) {
      console.error('Portföy listesi yüklenemedi:', error);
    }
  };

  const handlePortfolioSelect = async (portfolioId) => {
    setSelectedPortfolioId(portfolioId);

    if (!portfolioId) return;
  };

  const handleAdd = async () => {
    if (!selectedPortfolioId) {
      showError('Lütfen bir portföy seçiniz');
      return;
    }

    try {
      await api.post(`/customers/${customerId}/portfolio-interests`, {
        portfolio_id: selectedPortfolioId,
        notes: notes
      });

      // Seçilen portföyün detaylarını al
      const selectedPortfolio = allPortfolios.find(p => p.value === parseInt(selectedPortfolioId));
      if (!selectedPortfolio?.portfolio) return;

      const portfolio = selectedPortfolio.portfolio;

      // Portföy bilgilerinden müşteri tercihi oluştur
      try {
        const preferenceData = {
          customer_id: customerId,
          property_type: portfolio.property_type || '',
          residential_type: portfolio.residential_type || '',
          room_count: portfolio.room_count || '',
          facade: portfolio.facade || '',
          location_city: portfolio.location_city || '',
          location_district: portfolio.location_district || '',
          location_neighborhood: portfolio.location_neighborhood || '',
          min_floor: portfolio.floor || '',
          max_floor: portfolio.floor || '',
          has_elevator: false,
          min_sqm: portfolio.net_sqm || portfolio.gross_sqm || '',
          max_sqm: portfolio.net_sqm || portfolio.gross_sqm || '',
          min_price: portfolio.list_price || '',
          max_price: portfolio.list_price || '',
          min_building_age: portfolio.building_age || '',
          max_building_age: portfolio.building_age || '',
          additional_notes: `${portfolio.title} portföyüne göre oluşturuldu`
        };

        // Müşteri tercihine kaydet
        await api.post('/customer-property-preferences', preferenceData);
        showSuccess('Portföy bilgileri müşteri tercihlerine eklendi');
      } catch (error) {
        console.error('Tercih oluşturulamadı:', error);
        // Hata olsa bile devam et
      }


      showSuccess('Portföy ilgi listesine eklendi');
      fetchCustomerPortfolios();
      setShowForm(false);
      setSelectedPortfolioId('');
      setNotes('');
    } catch (error) {
      console.error('Portföy eklenemedi:', error);
      showError(error.response?.data?.message || 'Portföy eklenirken bir hata oluştu');
    }
  };

  const handleRemove = async (portfolioId) => {
    const result = await showDeleteConfirm('Bu portföyü ilgi listesinden');
    if (!result.isConfirmed) return;

    try {
      // Portföy bilgisini bul
      const portfolio = portfolios.find(p => p.id === portfolioId);

      // Portföyü ilgi listesinden çıkar
      await api.delete(`/customers/${customerId}/portfolio-interests/${portfolioId}`);

      // Bu portföyden oluşturulan müşteri tercihlerini bul ve sil
      if (portfolio) {
        try {
          // Müşterinin tüm tercihlerini al
          const preferencesResponse = await api.get(`/customers/${customerId}/property-preferences`);
          const preferences = preferencesResponse.data.data || preferencesResponse.data;

          // Portföy başlığını içeren tercihleri bul
          const relatedPreferences = preferences.filter(pref =>
            pref.additional_notes?.includes(`${portfolio.title} portföyüne göre oluşturuldu`)
          );

          // İlgili tercihleri sil
          for (const pref of relatedPreferences) {
            await api.delete(`/customer-property-preferences/${pref.id}`);
          }
        } catch (error) {
          console.error('Tercihler silinirken hata:', error);
          // Tercih silinmese bile portföy çıkarma işlemi başarılı
        }
      }

      showSuccess('Portföy ve ilgili tercihler silindi');
      fetchCustomerPortfolios();
    } catch (error) {
      console.error('Portföy çıkarılamadı:', error);
      showError('Portföy çıkarılırken bir hata oluştu');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  return (
    <Card
      title="İlgilendiği Portföyler"
      action={
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <Plus size={16} />
          Portföy Ekle
        </Button>
      }
    >
      {showForm && (
        <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="space-y-3">
            <Select
              label="Portföy Seç"
              name="portfolio_id"
              value={selectedPortfolioId}
              onChange={(e) => handlePortfolioSelect(e.target.value)}
              options={[
                { value: '', label: 'Seçiniz...' },
                ...allPortfolios.filter(p => !portfolios.some(ep => ep.id === p.value))
              ]}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notlar
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                rows={2}
                placeholder="İlgi notları..."
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd}>
                Ekle
              </Button>
              <Button size="sm" variant="secondary" onClick={() => {
                setShowForm(false);
                setSelectedPortfolioId('');
                setNotes('');
              }}>
                İptal
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {portfolios.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            Henüz ilgilenilen portföy eklenmemiş
          </p>
        ) : (
          portfolios.map((portfolio) => (
            <div
              key={portfolio.id}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex-1">
                <h4
                  className="font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors"
                  onClick={() => navigate(`/portfolios/${portfolio.id}`)}
                >
                  {portfolio.title}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {(portfolio.city === null) ? 'Şehir Yok' : portfolio.city.name} / {(portfolio.district === null) ? 'İlçe Yok' : portfolio.district.name}
                  {portfolio.room_count && ` • ${portfolio.room_count}`}
                  {portfolio.list_price && ` • ${portfolio.list_price.toLocaleString('tr-TR')} ₺`}
                </p>
                {portfolio.pivot?.notes && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Not: {portfolio.pivot.notes}
                  </p>
                )}
              </div>
              <div className="flex gap-2 ml-4">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate(`/portfolios/${portfolio.id}`)}
                >
                  <ExternalLink size={16} />
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleRemove(portfolio.id)}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

export default InterestedPortfolios;
